import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import classNames from "classnames";
import Log from "utils/log";

import TakeoverPanel from "components/library/TakeoverPanel";
import ReadOnlyWrapper from "editor/containers/ReadOnlyWrapper";
import TakeoverHeader from "components/library/TakeoverHeader";
import Button from "components/library/Button";
import Typeahead from "components/library/Typeahead";
import ExpandingTextarea from "components/library/ExpandingTextarea";
import TextInput from "components/library/TextInput";
import Label from "components/library/Label";
import {
  hydrateAlert,
  getAlertListAsyncForTypeahead,
  sendAlertAsync,
  saveAlertAsync,
} from "editor/actions/alert";
import { onChange } from "editor/actions/form";
import { setMessage } from "actions/message";
import { differentTabLocked } from "editor/actions/status";
import {
  ALERT_SUBJECT_PLACEHOLDER,
  ALERT_BODY_PLACEHOLDER,
  ALERT_SELECT_PLACEHOLDER,
} from "config/constants/editor";
import isTabLockError from "editor/utils/isTabLockError";
import { MESSAGES } from "config/constants";

import styles from "./AlertEditor.css";

class AlertEditor extends Component {
  constructor(props) {
    super(props);
    this.closeEditor = this._closeEditor.bind(this);
    this.saveAndSend = this._saveAndSend.bind(this);
    this.idPrefix = Math.random()
      .toString(36)
      .substring(7);

    this.state = {
      errors: {},
    };
  }

  getId(field) {
    return `${this.idPrefix}-${field}`;
  }

  _closeEditor() {
    this.props.history.go(-1);
  }

  _saveAndSend() {
    const currId = this.props.form.id;
    let alertList = this.props.form.alert_lists;
    return (
      this.props
        .saveAlertAsync()
        .then(() => {
          // error handling outside of a createAsyncAction
          // case: user saves before inputting recipients
          if (!alertList || alertList.length < 1) {
            alertList = [{ slug: "" }];
          }
          return this.props.sendAlertAsync(currId, alertList);
        })
        .then(response => {
          if (response.error) {
            throw response;
          }
          // if successful:
          // * send back to the dashboard
          // * display message
          this.props.history.replace("/dashboard/published");
          this.props.setMessage({
            message: "Your alert has been sent",
            type: MESSAGES.success,
          });
        })
        // catch errors from Sailthru API
        // reduce & display them
        .catch(error => {
          if (isTabLockError(error)) {
            return this.props.differentTabLocked();
          }

          // Update the state with the error. This will cause errors related to specific
          // fields on the form (if there are any) to display for the user.
          if ("context" in error) {
            // API errors pass a "context" field. "decodedJSON" contains any errors related
            // to specific fields on the form.
            this.setState({ errors: error.context.decodedJSON });
          } else {
            // If the error was caused by form validation, this will update the state with
            // field-specific errors.
            this.setState({ errors: error });
          }
          // catch & log when sendAlert fails
          // due to Sailthru
          Log.error(error);
          let errMessage = "Alert failed to send";

          // Try to get a descriptive error message from the error payload, if there is one.
          if (error.payload) {
            try {
              errMessage = JSON.parse(
                error.payload.message
              ).sailthru_responses.reduce((accum, resp) => {
                return accum.concat(resp.status, " ");
              }, "");
            } catch (e) {
              errMessage = error.payload.message;
            }
          }
          this.props.setMessage({
            message: errMessage,
            type: MESSAGES.failure,
          });
        })
        // Catch & log when saveAlert failes
        // due to the API or validation issues
        .catch(error => {
          Log.error(error);
        })
    );
  }

  componentDidMount() {
    this.props.hydrateAlert(this.props.match.params.id);
  }

  render() {
    return (
      <ReadOnlyWrapper
        id={this.props.match.params.id}
        status={this.props.form.status}
      >
        <TakeoverPanel containerStyle={styles.root}>
          <TakeoverHeader>Send alert</TakeoverHeader>

          <div className={styles.field}>
            <TextInput
              label="Subject line"
              id={this.getId("alert_subject")}
              autoFocus={true}
              className={styles.input}
              placeholder={ALERT_SUBJECT_PLACEHOLDER}
              value={this.props.form.alert_subject}
              onChange={this.props.onChange("alert_subject")}
              disabled={this.props.status.isReadOnly}
              error={this.state.errors.alert_subject}
            />
          </div>

          <div className={styles.field}>
            <Label htmlFor={this.getId("alert_text")}>Alert body</Label>
            <ExpandingTextarea
              id={this.getId("alert_text")}
              placeholder={ALERT_BODY_PLACEHOLDER}
              value={this.props.form.alert_text}
              onChange={this.props.onChange("alert_text")}
              disabled={this.props.status.isReadOnly}
              error={this.state.errors.alert_text}
            />
          </div>

          <div className={classNames(styles.field, styles.dropdown)}>
            <Label htmlFor={this.getId("alert_lists")}>Recipients</Label>
            <Typeahead
              inputId={this.getId("alert_lists")}
              value={this.props.form.alert_lists}
              onChange={this.props.onChange("alert_lists")}
              loadOptions={this.props.getAlertListAsyncForTypeahead}
              labelKey="name"
              valueKey="sailthru_list_id"
              placeholder={ALERT_SELECT_PLACEHOLDER}
              disabled={this.props.status.isReadOnly}
              error={this.state.errors.alert_lists}
            />
          </div>

          <div className={styles.note}>
            Subscribers to multiple lists will receive only one email
          </div>

          <Button
            type="submit"
            onClick={this.saveAndSend}
            isDisabled={this.props.status.isReadOnly}
          >
            Send alert
          </Button>
          <Button
            role="link"
            onClick={this.closeEditor}
            type="cancel"
            aria-label="Cancel editing an alert and go back to the text editor"
          >
            Cancel
          </Button>
        </TakeoverPanel>
      </ReadOnlyWrapper>
    );
  }
}

const mapStateToProps = state => {
  return {
    form: state.editor.form,
    status: state.editor.status,
  };
};

const mapDispatchToProps = {
  getAlertListAsyncForTypeahead,
  onChange,
  hydrateAlert,
  saveAlertAsync,
  sendAlertAsync,
  setMessage,
  differentTabLocked,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AlertEditor);

AlertEditor.propTypes = {
  id: PropTypes.string,
  hydrateAlert: PropTypes.func,
  getAlertListAsyncForTypeahead: PropTypes.func,
  message: PropTypes.object,
  onChange: PropTypes.func,
  saveAlertAsync: PropTypes.func,
  sendAlertAsync: PropTypes.func,
  setMessage: PropTypes.func,
  differentTabLocked: PropTypes.func.isRequired,
};
